import {useState} from 'react'
import {
  parseJSONWithBetterErrors,
  fetchWithErrorHandling,
  throwErrorsIfBadResponse,
} from '@github-ui/pull-request-page-data-tooling/fetch-error-handling'
import {
  pullRequestDismissAutomatedReviewCommentPath,
  pullRequestReopenAutomatedReviewCommentPath,
} from '@github-ui/paths'
import {useMutation} from '@github-ui/react-query'
import {BaseAutomatedReviewCommentActions} from './BaseAutomatedReviewCommentActions'
import type {AutomatedComment} from '../types'

export function AutomatedReviewCommentActions({
  automatedComment,
  basePath,
}: {
  automatedComment: AutomatedComment
  basePath: string
}) {
  // This state is used to continue disabling the button beyond form submission because a page reload gets triggered on success.
  const [isButtonDisabled, setIsButtonDisabled] = useState(false)

  const dismissMutation = useMutation({
    mutationFn: async ({reason, resolutionNote}: {reason: string; resolutionNote?: string}) => {
      const apiUrl = pullRequestDismissAutomatedReviewCommentPath({
        basePath,
        automatedReviewCommentId: automatedComment.id,
      })
      const response = await fetchWithErrorHandling(apiUrl, {
        method: 'PUT',
        body: {
          reason,
          resolution_note: resolutionNote,
        },
      })

      const json = await parseJSONWithBetterErrors(response)
      throwErrorsIfBadResponse(response, json)
      return json
    },
    onSuccess: () => {
      window.location.reload()
    },
    onError: () => {
      setIsButtonDisabled(false)
    },
  })

  const reopenMutation = useMutation({
    mutationFn: async () => {
      const apiUrl = pullRequestReopenAutomatedReviewCommentPath({
        basePath,
        automatedReviewCommentId: automatedComment.id,
      })
      const response = await fetchWithErrorHandling(apiUrl, {
        method: 'PUT',
      })

      const json = await parseJSONWithBetterErrors(response)
      throwErrorsIfBadResponse(response, json)
      return json
    },
    onSuccess: () => {
      window.location.reload()
    },
    onError: () => {
      setIsButtonDisabled(false)
    },
  })

  const onDismiss = (
    {reason, resolutionNote}: {reason: string; resolutionNote?: string},
    {onSuccess}: {onSuccess: () => void},
  ) => {
    setIsButtonDisabled(true)
    dismissMutation.mutate({reason, resolutionNote}, {onSuccess})
  }

  const onReopen = () => {
    setIsButtonDisabled(true)
    reopenMutation.mutate()
  }

  return (
    <BaseAutomatedReviewCommentActions
      automatedComment={automatedComment}
      dismissError={dismissMutation.error}
      isButtonDisabled={isButtonDisabled}
      onReopen={onReopen}
      onDismiss={onDismiss}
    />
  )
}
